import { isValidElement, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, FileCheck2, Gauge, Send, Sparkles, Timer } from "lucide-react";
import { fetchAnalyticsPulse } from "../../api/analyticsClient";
import { useNexusOptional } from "../../ai/NexusProvider";
import { LENS_ROUTES, ROUTES } from "../routes";

const PULSE_SNAPSHOT_KEY = "nexus.livePulse.snapshot";

const DASHBOARD_METRICS = [
  {
    key: "invitesSent",
    label: "Invites sent",
    to: LENS_ROUTES.portfolioOperations,
    icon: Send,
    statusColor: "sky",
  },
  {
    key: "inProgress",
    label: "In progress",
    to: LENS_ROUTES.engagementPipeline,
    icon: Timer,
    statusColor: "amber",
  },
  {
    key: "reportReady",
    label: "Report ready",
    to: LENS_ROUTES.portfolioPerformance,
    icon: FileCheck2,
    statusColor: "emerald",
  },
  {
    key: "avgScore",
    label: "Avg score",
    to: LENS_ROUTES.portfolioPerformance,
    suffix: "/100",
    nullable: true,
    icon: Gauge,
    statusColor: "violet",
  },
  {
    key: "companies",
    label: "Companies",
    to: LENS_ROUTES.studioCompanies,
    icon: Building2,
    statusColor: "teal",
  },
];

function formatMetricValue(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString();
  }
  return value;
}

function readPulseSnapshot() {
  try {
    const raw = window.localStorage.getItem(PULSE_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePulseSnapshot(pulse) {
  try {
    window.localStorage.setItem(PULSE_SNAPSHOT_KEY, JSON.stringify(pulse));
  } catch {
    /* ignore quota / private mode */
  }
}

function trendFromSnapshot(current, previous) {
  if (current == null || previous == null) return null;
  const curr = Number(current);
  const prev = Number(previous);
  if (!Number.isFinite(curr) || !Number.isFinite(prev) || prev === 0) return null;
  return Math.round(((curr - prev) / Math.abs(prev)) * 100);
}

function renderStatusIcon(icon) {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  const Icon = icon;
  return <Icon className="mini-data-card-icon" aria-hidden />;
}

export function MiniDataCard({
  label,
  value,
  suffix,
  trend,
  icon,
  statusColor = "sky",
  to,
  dark = false,
  loading = false,
}) {
  const numericTrend = trend == null || Number.isNaN(Number(trend)) ? null : Number(trend);
  const direction = numericTrend == null ? "live" : numericTrend > 0 ? "up" : numericTrend < 0 ? "down" : "flat";
  const trendText =
    numericTrend == null
      ? "Live"
      : `${numericTrend > 0 ? "+" : numericTrend < 0 ? "−" : ""}${Math.abs(numericTrend)}%`;
  const trendArrow = direction === "up" ? "↑" : direction === "down" ? "↓" : direction === "flat" ? "→" : "●";
  const trendAria =
    numericTrend == null
      ? "Live metric"
      : numericTrend === 0
        ? "Unchanged"
        : `${numericTrend > 0 ? "Up" : "Down"} ${Math.abs(numericTrend)} percent`;

  const card = (
    <article
      className={`mini-data-card mini-data-card--${dark ? "dark" : "light"} mini-data-card--${statusColor}${
        loading ? " mini-data-card--loading" : ""
      }`}
    >
      <div className="mini-data-card-top">
        <span className="mini-data-card-label">{label}</span>
        <span className={`mini-data-card-icon-badge mini-data-card-icon-badge--${statusColor}`} aria-hidden>
          {renderStatusIcon(icon)}
        </span>
      </div>
      <p className="mini-data-card-value">
        {formatMetricValue(value)}
        {suffix && value != null ? <span className="mini-data-card-suffix">{suffix}</span> : null}
      </p>
      <div className="mini-data-card-footer">
        <span className={`mini-data-card-trend mini-data-card-trend--${direction}`} aria-label={trendAria}>
          <span className="mini-data-card-trend-arrow" aria-hidden>
            {trendArrow}
          </span>
          {trendText}
        </span>
      </div>
    </article>
  );

  if (!to) return card;

  return (
    <Link to={to} className="mini-data-card-link" aria-label={`${label}: ${formatMetricValue(value)}`}>
      {card}
    </Link>
  );
}

function PulseCard({ label, value, suffix, to, dark }) {
  return (
    <Link
      to={to}
      className={`live-pulse-card group ${dark ? "live-pulse-card--dark" : "live-pulse-card--light"}`}
    >
      <span className="live-pulse-card-label">{label}</span>
      <span className="live-pulse-card-value">
        {value ?? "—"}
        {suffix ? <span className="live-pulse-card-suffix">{suffix}</span> : null}
      </span>
      <span className="live-pulse-card-arrow" aria-hidden>
        →
      </span>
    </Link>
  );
}

/** Live portfolio pulse for NEXUS command home — single lightweight API call. */
export default function LivePulseStrip({ dark = true, layout = "default" }) {
  const navigate = useNavigate();
  const nexus = useNexusOptional();
  const [pulse, setPulse] = useState(null);
  const [recommendedAction, setRecommendedAction] = useState(null);
  const [trends, setTrends] = useState({});
  const [loading, setLoading] = useState(true);
  const isDashboard = layout === "dashboard";

  const handleReviewRecommended = useCallback(() => {
    if (!recommendedAction?.workflowGuideId) return;
    nexus?.startNewChat?.();
    nexus?.bumpConversations?.();
    nexus?.setChatHandoff?.({
      mode: "workflow",
      guideId: recommendedAction.workflowGuideId,
      message: recommendedAction.workflowMessage || undefined,
    });
    navigate(ROUTES.nexusChat);
  }, [navigate, nexus, recommendedAction]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchAnalyticsPulse()
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || {};
        const nextPulse = {
          invitesSent: d.invitesSent ?? 0,
          inProgress: d.inProgress ?? 0,
          completed: d.completed ?? 0,
          reportReady: d.reportReady ?? 0,
          companies: d.companies ?? 0,
          avgScore: d.avgScore ?? null,
        };
        const previous = readPulseSnapshot();
        const nextTrends = {};
        DASHBOARD_METRICS.forEach((metric) => {
          nextTrends[metric.key] = previous
            ? trendFromSnapshot(nextPulse[metric.key], previous[metric.key])
            : null;
        });
        writePulseSnapshot(nextPulse);
        setPulse(nextPulse);
        setRecommendedAction(d.recommendedAction || null);
        setTrends(nextTrends);
      })
      .catch(() => {
        if (!cancelled) {
          setPulse(null);
          setRecommendedAction(null);
          setTrends({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className={`live-pulse-strip ${dark ? "live-pulse-strip--dark" : "live-pulse-strip--light"}${
        isDashboard ? " live-pulse-strip--dashboard" : ""
      }${loading ? " live-pulse-strip--loading" : ""}`}
      aria-label="Live workspace pulse"
    >
      <div className="live-pulse-strip-head">
        <h2 className="live-pulse-strip-title">Live pulse</h2>
        {isDashboard ? null : (
          <p className="live-pulse-strip-copy">Portfolio signals — select a metric to drill in</p>
        )}
      </div>
      {recommendedAction?.label ? (
        <div
          className={`live-pulse-recommended${dark ? " live-pulse-recommended--dark" : " live-pulse-recommended--light"}`}
        >
          <div className="live-pulse-recommended-copy">
            <span className="live-pulse-recommended-eyebrow">Recommended</span>
            <p className="live-pulse-recommended-headline">{recommendedAction.label}</p>
            {recommendedAction.description ? (
              <p className="live-pulse-recommended-desc">{recommendedAction.description}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="live-pulse-recommended-cta"
            onClick={handleReviewRecommended}
            disabled={loading}
          >
            <Sparkles size={16} aria-hidden />
            Review draft
          </button>
        </div>
      ) : null}
      {isDashboard ? (
        <div className="mini-data-card-grid">
          {DASHBOARD_METRICS.map((metric) => (
            <MiniDataCard
              key={metric.key}
              dark={dark}
              loading={loading}
              label={metric.label}
              value={pulse?.[metric.key]}
              suffix={metric.suffix && pulse?.[metric.key] != null ? metric.suffix : null}
              trend={trends[metric.key]}
              icon={metric.icon}
              statusColor={metric.statusColor}
              to={metric.to}
            />
          ))}
        </div>
      ) : (
        <div className="live-pulse-strip-grid">
          {DASHBOARD_METRICS.map((metric) => (
            <PulseCard
              key={metric.key}
              dark={dark}
              label={metric.label}
              value={pulse?.[metric.key]}
              suffix={metric.suffix && pulse?.[metric.key] != null ? metric.suffix : null}
              to={metric.to}
            />
          ))}
        </div>
      )}
    </section>
  );
}
